import React, { FC, useCallback } from 'react';
import ReactDOM from 'react-dom';
import OutsideClickHandler from 'react-outside-click-handler';
import { twMerge } from 'tailwind-merge';

import { VoidFn } from 'types/baseTypes';
import { useScreenDimensions } from 'hooks';

interface CustomModalProps {
  children: React.ReactNode;
  open: boolean;
  closeModal?: VoidFn;
  width?: string;
  height?: string;
  borderRadius?: string;
  backgroundColor?: string;
  overlayClassName?: string;
  widthInPx?: string;
  className?: string;
  enableOutsideClick?: boolean;
  onOutsideClick?: VoidFn;
}

const CustomModal: FC<CustomModalProps> = ({
  backgroundColor = 'bg-primary',
  overlayClassName = 'bg-[#00132480]',
  children,
  closeModal,
  open,
  borderRadius = 'rounded-md',
  width = '',
  height = '',
  widthInPx = '',
  className = '',
  enableOutsideClick = true,
  onOutsideClick
}) => {
  const { viewportHeightInPx } = useScreenDimensions();

  const handleOutsideClick = useCallback((): void => {
    if (enableOutsideClick) {
      if (onOutsideClick) {
        onOutsideClick();
        return;
      }
      closeModal?.();
    }
  }, [enableOutsideClick, onOutsideClick, closeModal]);

  const styles: React.CSSProperties = {};
  if (widthInPx) {
    styles.width = widthInPx;
  }

  if (!open) {
    return null;
  }

  const portalRoot = document.getElementById('portal-root');
  if (!portalRoot) {
    return null;
  }

  return ReactDOM.createPortal(
    // 'root' is added as an id inorder to access tailwind css from react portals
    <div id="root">
      <div
        style={{ height: viewportHeightInPx }}
        className={`fixed inset-0 z-[999] flex w-screen ${overlayClassName} `}>
        <div
          className={twMerge(
            `relative flex z-[999] m-auto overflow-y-auto min-h-[200px] max-h-[90vh] md:min-w-[341px] 
              w-11/12 md:w-[341px] bg-primary`,
            `${backgroundColor} ${borderRadius} ${width} ${height} ${className}`
          )}
          style={styles}>
          <div className="h-full w-full min-w-0 overflow-visible">
            <OutsideClickHandler onOutsideClick={handleOutsideClick} display="contents" disabled={!enableOutsideClick}>
              {children}
            </OutsideClickHandler>
          </div>
        </div>
      </div>
    </div>,
    portalRoot
  );
};

export default CustomModal;
